import { DateTime } from "luxon"
import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { DatesSetArg } from "@fullcalendar/core"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    fetchServiceCenterCalendarEvents,
    FetchServiceCenterCalendarEventsResponse,
    ServiceCenterCalendarQuery,
} from "../calendarApi"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../login/loginSelectors"
import { Grid } from "@mui/material"
import { StandardCalendar } from "../../../../../shared/components/calendar/StandardCalendar"
import { useDateTime } from "../../../../localization/useDateTime"
import { OrderSummary } from "../../../../nonAdminUser/reports/upcomingOrders/resourceBookings/userResourceBookingTypes"
import { CalendarEvent } from "../../../../../shared/components/calendar/calendarTypes"

type Props = {}

const useStyles = makeStyles()((theme) => ({
    container: {
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: "0.75rem",
        gap: "1.25rem",
        marginTop: "3rem",
        padding: "3rem 1rem 1rem 1rem",
    },
}))

const selectOrders = (data: FetchServiceCenterCalendarEventsResponse): CalendarEvent[] => {
    return (
        data?.orders.map((order) => ({
            id: order.id,
            uid: order.uid,
            title: order.calendarDetails.title,
            start: order.calendarDetails.start,
            end: order.calendarDetails.end,
            meetingDetails: {
                meetingName: order.calendarDetails.title,
            },
            deliveryDetails: order.deliveryDetails,
            extendedProps: {
                uid: order.uid,
                orderSummary: order.orderSummary,
                color: order.calendarDetails.color,
                participants: order.orderSummary.participants,
                bgColor: order.calendarDetails.bgColor,
                location: order.calendarDetails.location,
            },
        })) || []
    )
}

export function ServiceCenterCalendar({}: Props) {
    const { classes } = useStyles()
    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()

    const [startDate, setStartDate] = useState<DateTime>(today.startOf("month"))
    const [endDate, setEndDate] = useState<DateTime>(today.endOf("month"))

    const { data: events = [] } = useApiQuery(fetchServiceCenterCalendarEvents, {
        queryName: ServiceCenterCalendarQuery.EVENTS,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            startDate: startDate.toISODate(),
            endDate: endDate.toISODate(),
        },
        select: selectOrders,
    })

    const handleDatesSet = (datesSetInfo: DatesSetArg) => {
        setStartDate(DateTime.fromJSDate(datesSetInfo.start))
        setEndDate(DateTime.fromJSDate(datesSetInfo.end))
    }

    return (
        <Grid container flexDirection="column" className={classes.container}>
            <StandardCalendar events={events} handleDatesSet={handleDatesSet} />
        </Grid>
    )
}
